/**
 * Self-test suite for the encrypted file upload / download primitives.
 *
 * Exercises `encryptFile` / `decryptFile` from `file_encryption.ts` using
 * in-memory buffers only — no network calls, no DOM, no Blob.arrayBuffer()
 * IO beyond the SubtleCrypto operations themselves.
 *
 * iOS reference: `kaether_for_ios/Stoat/E2EE/MediaE2EE.swift`
 * (not present in this repo; wire-format mirror is documented in
 *  `kaether_for_web/docs/file-encryption.md`).
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * Auto-discovered by Vite `import.meta.glob` in `selftest/index.ts`.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  encryptFile,
  decryptFile,
} from "./file_encryption";
import type { EncryptedFileMetadata } from "./file_encryption";

// ---------------------------------------------------------------------------
// Helpers — build a File from a Uint8Array without real filesystem access
// ---------------------------------------------------------------------------

function makeFile(bytes: Uint8Array, name: string, mime: string): File {
  return new File([bytes], name, { type: mime });
}

/** Read a Blob back to a Uint8Array (SubtleCrypto path, pure-memory). */
async function blobToU8(blob: Blob): Promise<Uint8Array> {
  return new Uint8Array(await blob.arrayBuffer());
}

/** Byte-by-byte equality between two Uint8Arrays. */
function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/** Return a copy of metadata with the fileKey replaced by a freshly-generated one. */
async function withWrongKey(
  meta: EncryptedFileMetadata,
): Promise<EncryptedFileMetadata> {
  const wrongKey = crypto.getRandomValues(new Uint8Array(32));
  const wrongKeyB64 = btoa(String.fromCharCode(...wrongKey));
  return { ...meta, fileKey: wrongKeyB64 };
}

/** Flip one byte at `offset` in an ArrayBuffer (returns a new ArrayBuffer). */
function tamperedCopy(src: ArrayBuffer, offset: number): ArrayBuffer {
  const copy = src.slice(0);
  const view = new Uint8Array(copy);
  view[offset] ^= 0xff;
  return copy;
}

// ---------------------------------------------------------------------------
// Test cases
// ---------------------------------------------------------------------------

const cases: TestCase[] = [];

// ── 1. Small round-trip (16 bytes) ─────────────────────────────────────────

{
  const NAME = "round-trip: 16-byte plaintext";
  try {
    const plain = new Uint8Array(16);
    for (let i = 0; i < 16; i++) plain[i] = i;
    const file = makeFile(plain, "test.bin", "application/octet-stream");

    const { encryptedBytes, metadata } = await encryptFile(file);
    const decryptedBlob = await decryptFile(encryptedBytes, metadata);
    const recovered = await blobToU8(decryptedBlob);

    cases.push(
      bytesEqual(plain, recovered)
        ? pass(NAME)
        : fail(NAME, `recovered ${recovered.length} bytes, expected 16; first byte ${recovered[0]} != ${plain[0]}`),
    );
  } catch (e) {
    cases.push(fail(NAME, e instanceof Error ? e.message : String(e)));
  }
}

// ── 2. Large round-trip (1 MB) ──────────────────────────────────────────────

{
  const NAME = "round-trip: 1 MB plaintext";
  try {
    const plain = new Uint8Array(1 * 1024 * 1024);
    // Web Crypto caps `getRandomValues` at 65536 bytes per call — fill in chunks.
    const RAND_CHUNK = 0x10000;
    for (let i = 0; i < plain.byteLength; i += RAND_CHUNK) {
      crypto.getRandomValues(
        plain.subarray(i, Math.min(i + RAND_CHUNK, plain.byteLength)),
      );
    }
    const file = makeFile(plain, "big.bin", "application/octet-stream");

    const { encryptedBytes, metadata } = await encryptFile(file);
    const decryptedBlob = await decryptFile(encryptedBytes, metadata);
    const recovered = await blobToU8(decryptedBlob);

    cases.push(
      bytesEqual(plain, recovered)
        ? pass(NAME)
        : fail(NAME, `length mismatch: got ${recovered.length}, expected ${plain.length}`),
    );
  } catch (e) {
    cases.push(fail(NAME, e instanceof Error ? e.message : String(e)));
  }
}

// ── 3. Wrong key → explicit error ───────────────────────────────────────────

{
  const NAME = "wrong key: decryption throws, not silent garbage";
  try {
    const plain = new Uint8Array([1, 2, 3, 4, 5, 6, 7, 8]);
    const file = makeFile(plain, "test.bin", "application/octet-stream");
    const { encryptedBytes, metadata } = await encryptFile(file);

    const badMeta = await withWrongKey(metadata);

    let threw = false;
    try {
      await decryptFile(encryptedBytes, badMeta);
    } catch {
      threw = true;
    }

    cases.push(
      threw
        ? pass(NAME)
        : fail(NAME, "decryptFile did not throw with a mismatched key"),
    );
  } catch (e) {
    cases.push(fail(NAME, e instanceof Error ? e.message : String(e)));
  }
}

// ── 4. Tampered ciphertext → AES-GCM auth tag failure ───────────────────────

{
  const NAME = "tampered ciphertext: auth-tag failure surfaces as error";
  try {
    const plain = new Uint8Array(32);
    crypto.getRandomValues(plain);
    const file = makeFile(plain, "test.bin", "application/octet-stream");
    const { encryptedBytes, metadata } = await encryptFile(file);

    // Flip a byte near the middle of the ciphertext (well inside the payload,
    // not just the tag so this tests body tampering as well as tag tampering).
    const midpoint = Math.floor(encryptedBytes.byteLength / 2);
    const corrupted = tamperedCopy(encryptedBytes, midpoint);

    let threw = false;
    try {
      await decryptFile(corrupted, metadata);
    } catch {
      threw = true;
    }

    cases.push(
      threw
        ? pass(NAME)
        : fail(NAME, "decryptFile did not throw when ciphertext was modified"),
    );
  } catch (e) {
    cases.push(fail(NAME, e instanceof Error ? e.message : String(e)));
  }
}

// ── 5. Nonce reuse safety: same plaintext → different ciphertexts ────────────

{
  const NAME = "nonce reuse safety: two encryptions of identical plaintext differ";
  try {
    const plain = new Uint8Array(64);
    plain.fill(0x42);
    const file1 = makeFile(plain, "a.bin", "application/octet-stream");
    const file2 = makeFile(plain, "a.bin", "application/octet-stream");

    const { encryptedBytes: ct1, metadata: m1 } = await encryptFile(file1);
    const { encryptedBytes: ct2, metadata: m2 } = await encryptFile(file2);

    const u1 = new Uint8Array(ct1);
    const u2 = new Uint8Array(ct2);

    // Also check that IVs differ (random nonce per encryption).
    const ivsDiffer = m1.iv !== m2.iv;
    const ciphersDiffer = !bytesEqual(u1, u2);

    cases.push(
      ivsDiffer && ciphersDiffer
        ? pass(NAME)
        : fail(
            NAME,
            `ivsDiffer=${ivsDiffer}, ciphersDiffer=${ciphersDiffer} — nonce may be static`,
          ),
    );
  } catch (e) {
    cases.push(fail(NAME, e instanceof Error ? e.message : String(e)));
  }
}

// ── 6. Empty plaintext round-trip ───────────────────────────────────────────

{
  const NAME = "empty plaintext: encrypt + decrypt returns empty Uint8Array";
  try {
    const plain = new Uint8Array(0);
    const file = makeFile(plain, "empty.bin", "application/octet-stream");

    const { encryptedBytes, metadata } = await encryptFile(file);
    const decryptedBlob = await decryptFile(encryptedBytes, metadata);
    const recovered = await blobToU8(decryptedBlob);

    cases.push(
      recovered.length === 0
        ? pass(NAME)
        : fail(NAME, `expected 0 bytes, got ${recovered.length}`),
    );
  } catch (e) {
    cases.push(fail(NAME, e instanceof Error ? e.message : String(e)));
  }
}

// ── 7. Key length: generated key is 32 bytes (AES-256) ──────────────────────

{
  const NAME = "key length: file key is 32 bytes (AES-256-GCM)";
  try {
    const plain = new Uint8Array([0xde, 0xad, 0xbe, 0xef]);
    const file = makeFile(plain, "test.bin", "application/octet-stream");

    const { metadata } = await encryptFile(file);

    // The fileKey field is base64; decode and check the raw byte length.
    const rawKey = Uint8Array.from(atob(metadata.fileKey), (c) =>
      c.charCodeAt(0),
    );

    cases.push(
      rawKey.length === 32
        ? pass(NAME)
        : fail(NAME, `expected 32 bytes, got ${rawKey.length}`),
    );
  } catch (e) {
    cases.push(fail(NAME, e instanceof Error ? e.message : String(e)));
  }
}

// ── 8. IV / nonce length: 12 bytes (AES-GCM standard) ───────────────────────

{
  const NAME = "IV length: 12 bytes (AES-GCM 96-bit nonce)";
  try {
    const plain = new Uint8Array([0xca, 0xfe]);
    const file = makeFile(plain, "test.bin", "application/octet-stream");

    const { metadata } = await encryptFile(file);

    const rawIv = Uint8Array.from(atob(metadata.iv), (c) => c.charCodeAt(0));

    cases.push(
      rawIv.length === 12
        ? pass(NAME)
        : fail(NAME, `expected 12 bytes, got ${rawIv.length}`),
    );
  } catch (e) {
    cases.push(fail(NAME, e instanceof Error ? e.message : String(e)));
  }
}

// ── 9. Metadata fields preserved through round-trip ─────────────────────────

{
  const NAME = "metadata: originalName + mime + size survive encrypt/decrypt";
  try {
    const plain = new Uint8Array(100);
    const file = makeFile(plain, "photo.jpg", "image/jpeg");

    const { encryptedBytes, metadata } = await encryptFile(file);

    const nameOk = metadata.originalName === "photo.jpg";
    const mimeOk = metadata.mime === "image/jpeg";
    const sizeOk = metadata.size === 100;

    if (!nameOk || !mimeOk || !sizeOk) {
      cases.push(
        fail(
          NAME,
          `name=${metadata.originalName} mime=${metadata.mime} size=${metadata.size}`,
        ),
      );
    } else {
      // Also verify decrypted blob has correct MIME type.
      const blob = await decryptFile(encryptedBytes, metadata);
      cases.push(
        blob.type === "image/jpeg"
          ? pass(NAME)
          : fail(NAME, `blob.type="${blob.type}", expected "image/jpeg"`),
      );
    }
  } catch (e) {
    cases.push(fail(NAME, e instanceof Error ? e.message : String(e)));
  }
}

// ── 10. Missing MIME falls back to application/octet-stream ─────────────────

{
  const NAME = "mime fallback: empty mime → application/octet-stream";
  try {
    // File constructed without a type (empty string is what the browser
    // returns for unknown files, and the impl maps that to octet-stream).
    const plain = new Uint8Array([0x00]);
    const file = new File([plain], "noext");  // no type arg → ""

    const { metadata } = await encryptFile(file);

    cases.push(
      metadata.mime === "application/octet-stream"
        ? pass(NAME)
        : fail(NAME, `expected "application/octet-stream", got "${metadata.mime}"`),
    );
  } catch (e) {
    cases.push(fail(NAME, e instanceof Error ? e.message : String(e)));
  }
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Encrypted file upload/download",
  isVerified: true,
  run: async () => buildSuiteResult(cases),
});
